import { styled } from '@mui/material/styles';
import { SecureRoute, useOktaAuth } from '@okta/okta-react';
import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { useAccountState } from 'contexts/account/account-context';
import { ErrorHandlerContextProvider } from 'contexts/error-handler/ErrorHandlerContext';
import { SnackbarProvider } from 'contexts/snackbar/SnackbarContext';
import { routes } from 'views/dashboard/routes';
import { trackDatadogRumView } from 'utils/datadog-rum';

import Layout from 'components/layout/layout';
import LoadingSpinner from 'components/loading-spinner';
import MobileAppBanner from 'components/mobile-app-banner';
import ErrorsDialog from 'views/dashboard/components/error-snackbar';
import Login from 'views/dashboard/components/login';
import NotFound from 'views/dashboard/components/not-found';
import SelectCompanyDialog from 'views/dashboard/components/select-company';
import SuccessDialog from 'views/dashboard/components/success-snackbar';
import WarningsDialog from 'views/dashboard/components/warning-snackbar';

import { Box } from '@mui/material';
import 'assets/react-datasheet.css';
import { UserType } from 'types/shared/user-type.enum';
import googleTagManager from 'utils/google-tag-manager';
import { EasterEggProvider } from 'views/easter-eggs/context/easter-egg-context';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
}));

const AppContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  overflow: 'auto',
}));

type DashboardProps = {};

const Dashboard: React.FC<DashboardProps> = () => {
  const { authState } = useOktaAuth();
  const { account } = useAccountState();
  const history = useHistory();
  // trackViewsManually is on, so RUM needs an explicit view per navigation
  useEffect(() => {
    trackDatadogRumView(history.location.pathname, routes);
    return history.listen((location) => trackDatadogRumView(location.pathname, routes));
  }, [history]);

  useEffect(() => {
    const userId = account.currentUser?.id;
    if (!!userId) {
      googleTagManager.initLogin(); // init gtm with user id

      if (!!account.company) {
        googleTagManager.push({
          event: 'select_company',
          companyId: account.company.companyId,
          companyType: account.company.companyType,
          marketId: account.company.marketId,
          marketName: account.company.marketName,
          marketIndustry: account.company.aquaSpecies,
          userType: (account.currentUser?.userType as UserType) || '',
          pageUrl: window.location.pathname,
        });
      }
    }
  }, [account.currentUser]);

  return !authState ? (
    <></>
  ) : authState.isAuthenticated ? (
    account.isFetching ? (
      <Box display="flex" height="100vh">
        <LoadingSpinner />
      </Box>
    ) : account.company ? (
      <Root>
        <MobileAppBanner />
        <AppContainer>
          <ErrorHandlerContextProvider>
            <SnackbarProvider>
              <Layout>
                <EasterEggProvider>
                  <Switch>
                    {routes.map(({ path, component }, key) => (
                      <SecureRoute exact path={path} component={component} key={key} />
                    ))}
                    <Redirect exact from="/" to="/overview" />
                    <Redirect exact from="/benchmark-analytics" to="/benchmark-analytics/growth" />
                    <Redirect exact from="/delivery-board" to="/delivery-board" />
                    <Redirect exact from="/order-feed" to="/order-feed/supply-plan" />
                    <Redirect exact from="/site-settings" to="/site-settings/sites" />
                    <Redirect exact from="/site-settings/sites/general" to="/site-settings/sites/" />
                    <Redirect exact from="/site-settings/sites/contact-details" to="/site-settings/sites/" />
                    <Redirect exact from="/site-settings/sites/inventory-silo-management" to="/site-settings/sites/" />
                    <Redirect exact from="/sensors" to="/sensors/sites" />
                    <Redirect exact from="/powerbi" to="/powerbi/reports" />
                    <Redirect exact from="/profiles-strategies" to="/profiles-strategies/feed-strategies" />
                    <Redirect exact from="/planning-prognosis" to="/planning-prognosis/sites/active" />
                    <Redirect exact from="/planning-prognosis/sites" to="/planning-prognosis/sites/active" />
                    <Redirect exact from="/planning-prognosis/notifications" to="/planning-prognosis/notifications/active" />
                    <Redirect exact from="/feed-program-planner" to="/feed-program-planner/feed-programs" />
                    <Redirect exact from="/bluviu-nutrition" to="/bluviu-nutrition/scenarios" />
                    <Redirect exact from="/fish-check" to="/fish-check/welfare-scoring" />
                    <Redirect exact from="/feed-management" to="/feed-management/bestmix-feeds" />
                    <Redirect exact from="/production-systems" to="/production-systems/farm-overview" />
                    <Redirect exact from="/self-service" to="/self-service/feed-catalog" />
                    <Redirect exact from="/playground" to="/playground/supply-plan/sites" />
                    <Route component={NotFound} />
                  </Switch>
                  <SuccessDialog />
                  <ErrorsDialog />
                  <WarningsDialog />
                </EasterEggProvider>
              </Layout>
            </SnackbarProvider>
          </ErrorHandlerContextProvider>
        </AppContainer>
      </Root>
    ) : (
      <SelectCompanyDialog />
    )
  ) : (
    <Login />
  );
};

export default Dashboard;
